// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Reading the markup the docstrings are actually written in.
//
// These are Sphinx docstrings, so they carry reStructuredText: ``literals``,
// :func:`references`, .. note:: and .. code-block:: directives, bullet lists.
// Printed as they stand, the reader gets the markup instead of what it means -
// a sentence full of backticks and a code sample announced by a line of colons.
//
// Parsed here rather than in the extractor so the presentation can be changed
// without rebuilding and re-releasing the framework. The extractor's job is to
// carry the text across faithfully; deciding what it looks like is this side's.
// ==========================================================================

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong'; content: Inline[] }
  | { kind: 'ref'; text: string; role: string };

export type Block =
  | { kind: 'para'; content: Inline[] }
  | { kind: 'code'; text: string; language: string }
  | { kind: 'admonition'; level: 'note' | 'warning'; content: Block[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] };

// `todo` is a message from the author to the author. To somebody reading the
// reference it says "unfinished" and gives them nothing to do about it, so it
// is dropped rather than drawn.
const DROPPED = new Set(['todo', 'codeauthor', 'glossary', 'index', 'seealso']);

const DIRECTIVE = /^\s*\.\.\s+([a-zA-Z-]+)::\s*(.*)$/;
// Not every docstring here is written in reST. Some use markdown fences, and
// a fence read as inline literals produces a wall of stray backticks.
const FENCE = /^\s*```(\w*)\s*$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

/**
 * Inline markup, in one pass.
 *
 * Order inside the alternation is the whole trick: ``double`` has to be tried
 * before `single`, and a :role:`target` before the bare backticks it contains,
 * or the longer form is eaten by the shorter one.
 */
const inlineScanner = () =>
  /``([^`]+)``|:([a-zA-Z]+):`([^`]+)`|`([^`]+)`|\*\*([^*]+)\*\*/g;

export const parseInline = (text: string): Inline[] => {
  const out: Inline[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  // A fresh matcher each call: bold parses its own contents recursively, and a
  // shared one would have its position reset underneath it mid-scan.
  const scanner = inlineScanner();
  while ((match = scanner.exec(text)) !== null) {
    if (match.index > last) {
      out.push({ kind: 'text', text: text.slice(last, match.index) });
    }
    if (match[1] !== undefined) {
      out.push({ kind: 'code', text: match[1] });
    } else if (match[3] !== undefined) {
      const role = match[2];
      // A term is an English word with a glossary entry somewhere else. We do
      // not host that glossary, and setting it in a code face would claim it
      // is a symbol, which it is not - so it reads as the prose it is.
      out.push(
        role === 'term'
          ? { kind: 'text', text: match[3] }
          : { kind: 'ref', text: match[3], role }
      );
    } else if (match[4] !== undefined) {
      out.push({ kind: 'code', text: match[4] });
    } else if (match[5] !== undefined) {
      // Bold routinely wraps a literal - `**Why not ``Bone.hide``.**` - so its
      // contents are markup in their own right, not a plain string.
      out.push({ kind: 'strong', content: parseInline(match[5]) });
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    out.push({ kind: 'text', text: text.slice(last) });
  }
  return out;
};

const indentOf = (line: string): number => line.length - line.trimStart().length;

/**
 * The lines belonging to a directive, and where its body ends.
 *
 * Two shapes have to be read. Properly, the body is indented under the marker
 * and ends where the indentation does. But a build made before the extractor
 * preserved indentation flattened everything to the left margin, and those
 * releases are still being served - there, the only boundary left is a blank
 * line. Both are handled so the pages read correctly against either.
 */
const directiveBody = (
  lines: string[],
  start: number,
  markerIndent: number
): { body: string[]; next: number } => {
  let index = start;
  while (index < lines.length && !lines[index].trim()) index += 1;
  if (index >= lines.length) return { body: [], next: index };

  const indented = indentOf(lines[index]) > markerIndent;
  const body: string[] = [];

  if (indented) {
    const bodyIndent = indentOf(lines[index]);
    while (index < lines.length) {
      const line = lines[index];
      if (!line.trim()) {
        body.push('');
        index += 1;
        continue;
      }
      if (indentOf(line) < bodyIndent) break;
      body.push(line.slice(bodyIndent));
      index += 1;
    }
  } else {
    // Flattened: take everything up to the first blank line.
    while (index < lines.length && lines[index].trim()) {
      body.push(lines[index].trim());
      index += 1;
    }
  }

  while (body.length && !body[body.length - 1].trim()) body.pop();
  return { body, next: index };
};

export const parseBlocks = (text: string): Block[] => {
  const lines = (text || '').split('\n');
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE.test(lines[index])) {
        body.push(lines[index]);
        index += 1;
      }
      index += 1; // the closing fence
      blocks.push({
        kind: 'code',
        text: body.join('\n').replace(/\s+$/, ''),
        language: fence[1] || 'text'
      });
      continue;
    }

    const directive = DIRECTIVE.exec(line);
    if (directive) {
      const name = directive[1].toLowerCase();
      const argument = directive[2].trim();
      const { body, next } = directiveBody(lines, index + 1, indentOf(line));
      index = next;

      if (DROPPED.has(name)) continue;

      if (name === 'code-block' || name === 'code' || name === 'literalinclude') {
        blocks.push({
          kind: 'code',
          text: body.join('\n'),
          language: argument || 'text'
        });
      } else if (name === 'note' || name === 'warning' || name === 'important') {
        blocks.push({
          kind: 'admonition',
          level: name === 'note' ? 'note' : 'warning',
          content: parseBlocks(body.join('\n'))
        });
      } else if (body.length) {
        // An unfamiliar directive still has prose in it worth showing; only
        // its marker is dropped.
        blocks.push(...parseBlocks(body.join('\n')));
      }
      continue;
    }

    if (BULLET.test(line) || NUMBERED.test(line)) {
      const ordered = !BULLET.test(line);
      const items: Inline[][] = [];
      while (index < lines.length) {
        const current = lines[index];
        const bullet = ordered ? NUMBERED.exec(current) : BULLET.exec(current);
        if (bullet) {
          items.push(parseInline(bullet[1].trim()));
          index += 1;
        } else if (current.trim() && indentOf(current) > 0 && items.length) {
          // A wrapped continuation of the item above it.
          const tail = items[items.length - 1];
          tail.push(...parseInline(' ' + current.trim()));
          index += 1;
        } else {
          break;
        }
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    // A paragraph, up to the next blank line or block opener.
    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index];
      if (
        !current.trim() ||
        DIRECTIVE.test(current) ||
        FENCE.test(current) ||
        BULLET.test(current) ||
        NUMBERED.test(current)
      ) {
        break;
      }
      paragraph.push(current.trim());
      index += 1;
    }

    let joined = paragraph.join('\n');
    let literal: Block | null = null;

    // `Something like this::` introduces a literal block. The doubled colon is
    // markup, not punctuation - left in, 113 sentences end in what reads as a
    // typo.
    if (/::\s*$/.test(joined)) {
      joined = joined.replace(/\s*::\s*$/, ':');
      const { body, next } = directiveBody(
        lines,
        index,
        indentOf(lines[index - 1] ?? '')
      );
      if (body.length) {
        literal = { kind: 'code', text: body.join('\n'), language: 'text' };
        index = next;
      }
    }

    if (joined.trim()) {
      blocks.push({ kind: 'para', content: parseInline(joined) });
    }
    if (literal) blocks.push(literal);
  }

  return blocks;
};

/** Whether anything in here needs room - used to decide what stays folded. */
export const hasBlockContent = (blocks: Block[]): boolean =>
  blocks.some((block) => block.kind !== 'para');

export const hasCode = (blocks: Block[]): boolean =>
  blocks.some(
    (block) =>
      block.kind === 'code' ||
      (block.kind === 'admonition' && hasCode(block.content))
  );
